import React, { useState, useEffect } from 'react';
import { 
  Truck, DollarSign, Calendar, User, Check, Clock, Package,
  RefreshCw, Search, Filter, Download, AlertCircle, CheckCircle,
  Loader2, ChevronDown, Car, FileText
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DriverFeesPortal = ({ onBack, user }) => {
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [trailerDetails, setTrailerDetails] = useState({
    type: '',
    license_plate: '',
    capacity: '',
    make: '',
    model: '',
    year: ''
  });

  const token = localStorage.getItem('auth_token');
  const isDriver = user?.role === 'driver';
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'staff';

  // Fetch fees
  const fetchFees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedDriver) params.append('driver_id', selectedDriver);
      if (selectedDate) params.append('date', selectedDate);

      const res = await fetch(`${API_URL}/api/driver/fees?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFees(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch fees');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all drivers (admin only)
  const fetchDrivers = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/drivers/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDrivers(data || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  // Mark fee as paid
  const markAsPaid = async (feeId) => {
    try {
      const res = await fetch(`${API_URL}/api/driver/fees/${feeId}/mark-paid`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Fee marked as paid');
        fetchFees();
      } else {
        throw new Error('Failed to mark as paid');
      }
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  // Acknowledge payment
  const acknowledgePayment = async (feeId) => {
    try {
      const res = await fetch(`${API_URL}/api/driver/fees/${feeId}/acknowledge`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Payment acknowledged');
        fetchFees();
      } else {
        throw new Error('Failed to acknowledge');
      }
    } catch (error) {
      toast.error('Failed to acknowledge payment');
    }
  };

  // Update trailer details
  const updateTrailerDetails = async () => {
    try {
      const res = await fetch(`${API_URL}/api/driver/profile/trailer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(trailerDetails)
      });
      
      if (res.ok) {
        toast.success('Trailer details updated');
        setShowTrailerModal(false);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update trailer details');
    }
  };

  useEffect(() => {
    fetchFees();
    fetchDrivers();
  }, [selectedDriver, selectedDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const totalFees = fees.reduce((sum, f) => sum + (f.total_fee || 0), 0);
  const totalDeliveries = fees.reduce((sum, f) => sum + (f.deliveries_count || 0), 0);
  const paidFees = fees.filter(f => f.is_paid).length;
  const acknowledgedFees = fees.filter(f => f.driver_acknowledged).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} className="text-white hover:bg-white/20" />
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <DollarSign className="w-7 h-7" />
                  Driver Delivery Fees
                </h1>
                <p className="text-cyan-100 text-sm">
                  {isDriver ? 'View your delivery earnings' : 'Manage driver delivery fees'}
                </p>
              </div>
            </div>
            {isDriver && (
              <Button
                onClick={() => setShowTrailerModal(true)}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Car className="w-4 h-4 mr-2" />
                My Trailer Details
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {isAdmin && (
              <div className="min-w-[200px]">
                <Label className="text-sm text-gray-600">Driver</Label>
                <select
                  value={selectedDriver}
                  onChange={(e) => setSelectedDriver(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Drivers</option>
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name} ({driver.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label className="text-sm text-gray-600">Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={fetchFees}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalFees)}</p>
                <p className="text-sm text-gray-500">Total Fees</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalDeliveries}</p>
                <p className="text-sm text-gray-500">Deliveries</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{paidFees}/{fees.length}</p>
                <p className="text-sm text-gray-500">Paid</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Check className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{acknowledgedFees}</p>
                <p className="text-sm text-gray-500">Acknowledged</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Fees Table */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-900">Delivery Fee Records</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mx-auto" />
              <p className="text-gray-500 mt-2">Loading fees...</p>
            </div>
          ) : fees.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No fee records found</p>
              <p className="text-sm text-gray-400">
                {isDriver 
                  ? 'Your delivery fees will appear here after completing deliveries'
                  : 'Fee records will appear after drivers complete deliveries'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    {isAdmin && (
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Driver</th>
                    )}
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Deliveries</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fee</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Trailer</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {fees.map((fee) => (
                    <tr key={fee.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(fee.date).toLocaleDateString()}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{fee.driver_name}</p>
                            <p className="text-xs text-gray-500">{fee.driver_email}</p>
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {fee.deliveries_count} deliveries
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-lg font-bold text-green-600">
                          {formatCurrency(fee.total_fee)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {fee.trailer_details?.type || '-'}
                        {fee.trailer_details?.license_plate && (
                          <span className="text-xs text-gray-400 block">
                            {fee.trailer_details.license_plate}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {fee.is_paid ? (
                            <Badge className="bg-green-100 text-green-700 w-fit">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Paid
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700 w-fit">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {fee.driver_acknowledged && (
                            <Badge className="bg-purple-100 text-purple-700 w-fit text-xs">
                              Driver Acknowledged
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {isAdmin && !fee.is_paid && (
                            <Button
                              size="sm"
                              onClick={() => markAsPaid(fee.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                          {isDriver && fee.is_paid && !fee.driver_acknowledged && (
                            <Button
                              size="sm"
                              onClick={() => acknowledgePayment(fee.id)}
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Acknowledge
                            </Button>
                          )}
                          {fee.paid_by && (
                            <span className="text-xs text-gray-400">
                              Paid by: {fee.paid_by}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Trailer Details Modal */}
      {showTrailerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Car className="w-5 h-5" />
                Trailer Details
              </h2>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vehicle Type</Label>
                  <Input
                    value={trailerDetails.type}
                    onChange={(e) => setTrailerDetails(p => ({ ...p, type: e.target.value }))}
                    placeholder="e.g., Van, Truck"
                  />
                </div>
                <div>
                  <Label>License Plate</Label>
                  <Input
                    value={trailerDetails.license_plate}
                    onChange={(e) => setTrailerDetails(p => ({ ...p, license_plate: e.target.value }))}
                    placeholder="e.g., ABC 123"
                  />
                </div>
                <div>
                  <Label>Make</Label>
                  <Input
                    value={trailerDetails.make}
                    onChange={(e) => setTrailerDetails(p => ({ ...p, make: e.target.value }))}
                    placeholder="e.g., Ford"
                  />
                </div>
                <div>
                  <Label>Model</Label>
                  <Input
                    value={trailerDetails.model}
                    onChange={(e) => setTrailerDetails(p => ({ ...p, model: e.target.value }))}
                    placeholder="e.g., Transit"
                  />
                </div>
                <div>
                  <Label>Year</Label>
                  <Input
                    value={trailerDetails.year}
                    onChange={(e) => setTrailerDetails(p => ({ ...p, year: e.target.value }))}
                    placeholder="e.g., 2022"
                  />
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input
                    value={trailerDetails.capacity}
                    onChange={(e) => setTrailerDetails(p => ({ ...p, capacity: e.target.value }))}
                    placeholder="e.g., 500 kg"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTrailerModal(false)}>
                Cancel
              </Button>
              <Button onClick={updateTrailerDetails} className="bg-cyan-600 hover:bg-cyan-700">
                Save Details
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DriverFeesPortal;
